import React, { useState, useEffect } from 'react';
import { apiCall } from '../AuthContext';
import { Plus, Trash2, Edit2, HelpCircle, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';

export default function AdminFAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: '', answer: '' });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchFaqs = async () => {
    try {
      const res = await apiCall('/api/faqs', { method: 'GET' });
      setFaqs(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchFaqs(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await apiCall(`/api/faqs/${editing}`, { method: 'PUT', data: form });
      } else {
        await apiCall('/api/faqs', { method: 'POST', data: form });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ question: '', answer: '' });
      fetchFaqs();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving FAQ');
    } finally { setSaving(false); }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await apiCall(`/api/faqs/${faqId}`, { method: 'DELETE' });
      fetchFaqs();
    } catch { alert('Failed to delete'); }
  };

  const openEdit = (faq) => {
    setEditing(faq.id);
    setForm({ question: faq.question, answer: faq.answer });
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-faq-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">FAQ Management</h2>
          <p className="text-sm text-slate-500 mt-1">Create and manage frequently asked questions</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ question: '', answer: '' }); }}
          className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-4 py-2.5 text-sm transition-colors"
          data-testid="create-faq-button">
          <Plus className="w-4 h-4" /> New FAQ
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="faq-form-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#0F172A]">{editing ? 'Edit FAQ' : 'Create FAQ'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Question</label>
                <input value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} required
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="How do I..." data-testid="faq-question-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Answer</label>
                <textarea value={form.answer} onChange={e => setForm(p => ({ ...p, answer: e.target.value }))} required rows={4}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm resize-none"
                  placeholder="Answer to the question..." data-testid="faq-answer-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 py-2.5 rounded-lg font-medium text-sm border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="faq-submit-button"
                  className="flex-1 bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAQ List */}
      {faqs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-faqs">
          <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No FAQs yet</p>
          <p className="text-sm text-slate-400 mt-1">Create your first FAQ to help your users</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="faq-list">
          {faqs.map(faq => (
            <div key={faq.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`faq-item-${faq.id}`}>
              <div className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <HelpCircle className="w-5 h-5 text-[#0052FF] flex-shrink-0" />
                  <p className="text-sm font-medium text-[#0F172A] truncate">{faq.question}</p>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); openEdit(faq); }} className="text-slate-400 hover:text-[#0052FF] p-1"
                    data-testid={`edit-faq-${faq.id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(faq.id); }} className="text-slate-400 hover:text-red-500 p-1"
                    data-testid={`delete-faq-${faq.id}`}><Trash2 className="w-4 h-4" /></button>
                  {expandedId === faq.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedId === faq.id && (
                <div className="px-6 pb-4 pt-0 border-t border-slate-100">
                  <p className="text-sm text-slate-600 leading-relaxed mt-3">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
